import React, { useEffect, useState, useMemo } from 'react';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { Row, Col, Container } from 'reactstrap';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import moment from 'moment';
import TimetableIcon from 'mdi-react/TimetableIcon';
import renderField from '../../../shared/components/custom/Field';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import validateQuestionaireForm from './validateQuestionaireForm';
import { partial } from '../../../util/translation/translation';
import { getImageHash } from '../../../util/functions';
import renderSelectField from '../../../shared/components/form/Select';
import TextEditor2 from '../../../shared/components/text-editor/TextEditor2';
import { connect } from 'react-redux';
import TooltipWithIcon from '../../../shared/components/custom/TooltipWithIcon';
import FormBox from '../../../shared/components/custom/FormBox';
import SwitchRow from '../../../shared/components/custom/SwitchRow';

const QuestionaireForm = ({
  questionaire,
  handleSubmit,
  initialize,
  questionaireCategories,
  change,
  leadCompanies,
  questionaireFormValues,
}) => {
  const s = partial('shared');
  const p = partial('questionaire');
  const q = partial('question');
  const [questionaireType, setQuestionaireType] = useState(
    questionaire?.type || 'QUESTIONAIRE'
  );
  // A poll is one question answered in a tap: it only needs a title, its
  // dates, an optional image and how votes work (and access)
  const isPoll = questionaireType === 'POLL';
  const questionaireTypes = ['QUESTIONAIRE', 'QUIZ', 'POLL'].map((item) => ({
    label: p(`questionaireType-${item}`),
    value: item,
  }));
  const questionaireCategoriesOptions = useMemo(
    () =>
      questionaireCategories.map((item) => ({
        label: item.title,
        value: item.id,
      })),
    [questionaireCategories]
  );

  const leadCompaniesOptions = useMemo(
    () =>
      leadCompanies.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [leadCompanies]
  );
  const answerVisibilityOptions = [
    { label: p('answerVisibility-NOT_VISIBLE'), value: 'NOT_VISIBLE' },
    {
      label: p('answerVisibility-SHOW_DURING_QUIZ'),
      value: 'SHOW_DURING_QUIZ',
    },
    {
      label: p('answerVisibility-SHOW_AFTER_ANSWERED'),
      value: 'SHOW_AFTER_ANSWERED',
    },
    {
      label: p('answerVisibility-SHOW_AFTER_ENDED'),
      value: 'SHOW_AFTER_ENDED',
    },
  ];

  // A new questionnaire starts active, editable and with a leaderboard
  // (the switches used to set this themselves when they appeared)
  useEffect(() => {
    if (!questionaire) {
      initialize({
        visible: true,
        editAnswers: true,
        leaderBoardVisibility: true,
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (questionaire) {
      setQuestionaireType(questionaire.type);
      initialize({
        headingImage: questionaire.imageUrl
          ? {
              name: 'questionaire image',
              preview: questionaire.imageUrl,
              croppedImage: questionaire.imageUrl,
              hash: getImageHash(questionaire.imageUrl),
            }
          : undefined,
        title: questionaire.title,
        description: questionaire.description,
        visible: !!questionaire.visible,
        editAnswers: !!questionaire.editAnswers,
        leaderBoardVisibility: questionaire.leaderBoardVisibility ?? true,
        startDate: moment(questionaire.startDate).format('YYYY-MM-DDTHH:mm'),
        endDate: moment(questionaire.endDate).format('YYYY-MM-DDTHH:mm'),
        type: {
          label: p(`questionaireType-${questionaire.type}`),
          value: questionaire.type,
        },
        dynamicLink: questionaire.dynamicLink,
        subtitle: questionaire.subtitle,
        category: questionaireCategoriesOptions.find(
          (item) => item.value === questionaire?.category?.id
        ),
        leadCompany: leadCompaniesOptions.find(
          (item) => item.value === questionaire?.leadCompany?.id
        ),
        answerVisibility: answerVisibilityOptions.find(
          (item) => item.value === questionaire?.answerVisibility
        ),
        ownerEmail: questionaire?.ownerEmail?.email,
      });
    }
  }, [questionaire, questionaireCategories, leadCompaniesOptions]);
  return (
    <form className="form" onSubmit={handleSubmit}>
      <Container>
        <Row>
          <Col lg={6}>
            <FormBox title={s('details')} className="mb-3">
              <div className="form-box__body">
                {/* The type decides which options apply, so it comes first */}
                <div className="form__form-group">
                  <span className="form-header">{p('selectType')}</span>
                  <Field
                    name="type"
                    placeholder={p('selectType')}
                    options={questionaireTypes}
                    component={renderSelectField}
                    disabled={questionaire?.id}
                    onChange={(e) => {
                      setQuestionaireType(e.value);
                      if (e.value === 'QUIZ') {
                        change('editAnswers', false);
                      }
                      // A poll shows the votes once you've voted and has no
                      // leaderboard
                      if (e.value === 'POLL') {
                        change('leaderBoardVisibility', false);
                        change('answerVisibility', {
                          label: p('answerVisibility-SHOW_AFTER_ANSWERED'),
                          value: 'SHOW_AFTER_ANSWERED',
                        });
                      }
                    }}
                  />
                  {isPoll && (
                    <small className="form__form-group-description">
                      {p('pollInfo')}
                    </small>
                  )}
                </div>
                <div className="form__form-group">
                  <span className="form-header">{s('title')}*</span>
                  <Field
                    name="title"
                    component={renderField}
                    placeholder={s('title')}
                  />
                </div>
                {!isPoll && (
                  <>
                    <div className="form__form-group">
                      <span className="form-header">{s('subtitle')}*</span>
                      <Field
                        name="subtitle"
                        component={renderField}
                        placeholder={s('subtitle')}
                      />
                    </div>
                    <div className="form__form-group">
                      <span className="form-header">{s('description')}*</span>
                      <Field
                        name="description"
                        component={TextEditor2}
                        header={[2, 3, false]}
                        placeholder={s('description')}
                      />
                    </div>
                  </>
                )}
                <div className="form-grid">
                  <div className="form__form-group">
                    <span className="form-header">{s('startDate')}*</span>
                    <div className="form__form-group-field">
                      <div className="form__form-group-icon">
                        <TimetableIcon />
                      </div>
                      <Field
                        name="startDate"
                        component={renderField}
                        type="datetime-local"
                      />
                    </div>
                  </div>
                  <div className="form__form-group">
                    <span className="form-header">{s('endDate')}*</span>
                    <div className="form__form-group-field">
                      <div className="form__form-group-icon">
                        <TimetableIcon />
                      </div>
                      <Field
                        name="endDate"
                        component={renderField}
                        type="datetime-local"
                      />
                    </div>
                  </div>
                  {!isPoll && (
                    <div className="form__form-group">
                      <span className="form-header">
                        {p('questionaireCategories.questionaireCategory')}
                      </span>
                      <Field
                        name="category"
                        placeholder={p('questionaireCategories.selectCategory')}
                        options={questionaireCategoriesOptions}
                        component={renderSelectField}
                      />
                    </div>
                  )}
                  {!isPoll && leadCompaniesOptions.length > 0 && (
                    <div className="form__form-group">
                      <span className="form-header">{p('leadCompany')}</span>
                      <Field
                        name="leadCompany"
                        placeholder={p('leadCompany')}
                        options={leadCompaniesOptions}
                        component={renderSelectField}
                        onChange={() => {
                          change('editAnswers', false);
                          change('answerVisibility', {
                            label: p('answerVisibility-NOT_VISIBLE'),
                            value: 'NOT_VISIBLE',
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </FormBox>
          </Col>
          <Col lg={6}>
            <FormBox
              title={isPoll ? `${s('image')} (${s('optional')})` : s('image')}
              className="mb-3"
            >
              <div className="form-box__body">
                <div className="form__form-group">
                  <Field
                    saveBase64Cropped
                    name="headingImage"
                    maxSize={3}
                    component={renderDropZoneField}
                    displayCrop
                    crop="BANNER_CROP"
                  />
                  <small className="form__form-group-description">
                    <strong>{s('note')}:</strong> {s('recommendedImageSize')}{' '}
                    16:9 (1035 x 582 px)
                  </small>
                </div>
              </div>
            </FormBox>

            <FormBox title={q('options')} className="mb-3">
              <div className="form-box__body">
                <div className="option-list mb-3">
                  <Field
                    name="visible"
                    component={SwitchRow}
                    label={s('active')}
                  />
                  <Field
                    name="editAnswers"
                    component={SwitchRow}
                    label={isPoll ? p('pollChangeVote') : p('editAnswers')}
                    disabled={
                      questionaireType === 'QUIZ' ||
                      !!questionaireFormValues?.leadCompany
                    }
                  />
                  {!isPoll && (
                    <Field
                      name="leaderBoardVisibility"
                      component={SwitchRow}
                      label={p('showLeaderboard')}
                      disabled={questionaireType === 'QUESTIONAIRE'}
                    />
                  )}
                </div>
                <div className="form__form-group">
                  <span className="form-header">
                    {isPoll
                      ? p('pollResultsVisibility')
                      : p('answerVisibility')}
                    *
                  </span>
                  <Field
                    name="answerVisibility"
                    placeholder={p('selectAnswerVisibility')}
                    options={answerVisibilityOptions}
                    defaultValue={answerVisibilityOptions[0]}
                    disabled={!!questionaireFormValues?.leadCompany}
                    component={renderSelectField}
                  />
                </div>
              </div>
            </FormBox>

            {!isPoll && (
              <FormBox title={p('notifyWhenAnsered')} className="mb-3">
                <div className="form-box__body">
                  <div className="form__form-group">
                    <span className="form-header">
                      {s('email')}&nbsp;
                      <TooltipWithIcon
                        id="question-owner-email"
                        text={p('notifyWhenAnseredInfo')}
                      />
                    </span>
                    <Field
                      name="ownerEmail"
                      component={renderField}
                      placeholder={s('email')}
                    />
                  </div>
                </div>
              </FormBox>
            )}

            {questionaire && (
              <FormBox title={s('universalLink')} className="mb-3">
                <div className="form-box__body">
                  <div className="form__form-group">
                    <div className="form__form-group-field">
                      <Field name="dynamicLink" component="input" disabled />
                      <button
                        type="button"
                        className="form__form-group-icon"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            questionaire.dynamicLink
                          )
                        }
                      >
                        <LinkVariantIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </FormBox>
            )}
          </Col>
        </Row>
      </Container>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'questionaire_form',
  validate: validateQuestionaireForm,
});

const mapStateToProps = (state) => ({
  questionaireFormValues: getFormValues('questionaire_form')(state),
});

export default connect(mapStateToProps)(reduxF(QuestionaireForm));
